// PlanSelectionStep.tsx
// Step 2: Choose tier (Platinum vs AI Platinum) and billing period (Monthly vs Annual)

import React, { useState } from 'react';
import { View, ScrollView, StyleSheet, Pressable, ActivityIndicator } from 'react-native';
import { Text, Button } from '../../../components/ui';
import { Theme } from '../../../utils/theme';
import { PaywallHeader } from '../components/PaywallHeader';
import { PlanCard } from '../components/PlanCard';
import { LegalFooter } from '../components/LegalFooter';
import type { PaywallStepProps, SubscriptionTierChoice, BillingPeriodChoice } from '../types';
import { PurchasesPackage, SubscriptionService } from '../../../services/subscriptionService';
import { useSubscription } from '../../../hooks/useSubscription';

interface PlanSelectionStepProps extends PaywallStepProps {
  isPurchasing: boolean;
  onPurchase: (packageToPurchase?: any) => void;
  onRestore: () => void;
  isRestoring: boolean;
}

export const PlanSelectionStep: React.FC<PlanSelectionStepProps> = ({
  selection,
  onUpdateSelection,
  onClose,
  offerings,
  isPurchasing,
  onPurchase,
  onRestore,
  isRestoring,
}) => {
  const { tier } = useSubscription();
  const hasPlatinum = tier === 'standard_premium'; // User already has Platinum, show only AI Platinum
  
  // If user has Platinum, default to AI tier (upgrade flow)
  const [localTier, setLocalTier] = useState<SubscriptionTierChoice>(hasPlatinum ? 'ai' : selection.tier);
  const [localPeriod, setLocalPeriod] = useState<BillingPeriodChoice>(selection.period);
  const [isInitialized, setIsInitialized] = useState(false);

  // Resolve package based on tier and period
  const resolvePackage = (
    targetTier: SubscriptionTierChoice,
    period: BillingPeriodChoice
  ): PurchasesPackage | null => {
    if (!offerings) return null;

    // Map tier to offering ID:
    // - standard tier → 'platinum' offering (Platinum - content only)
    // - ai tier → 'ai-platinum' offering (AI Platinum - content + AI)
    const offeringId = targetTier === 'standard' ? 'platinum' : 'ai-platinum';
    const periodKey = period === 'monthly' ? 'monthly' : 'annual';

    const allOfferings = offerings.all || {};
    const currentOffering = offerings.current;

    const offering =
      allOfferings[offeringId] ||
      (currentOffering?.identifier === offeringId ? currentOffering : null);

    if (!offering) return null;

    // Try packageType first
    const packageTypeTarget = period === 'monthly' ? 'MONTHLY' : 'ANNUAL';
    const byType = offering.availablePackages.find(
      (p: any) => (p.packageType || '').toUpperCase() === packageTypeTarget
    );
    if (byType) return byType;

    // Fallback: identifier contains monthly/annual
    const byId = offering.availablePackages.find((p: any) =>
      (p.identifier || '').toLowerCase().includes(periodKey)
    );
    if (byId) return byId;

    // Last resort: first available
    return offering.availablePackages[0] || null;
  };

  const getPrice = (tier: SubscriptionTierChoice, period: BillingPeriodChoice): string => {
    const pkg = resolvePackage(tier, period);
    return pkg?.product.priceString || '...';
  };

  const getHasTrial = (tier: SubscriptionTierChoice, period: BillingPeriodChoice): boolean => {
    const pkg = resolvePackage(tier, period);
    return pkg ? SubscriptionService.hasTrialOffer(pkg) : false;
  };

  const handlePurchase = () => {
    const pkg = resolvePackage(localTier, localPeriod);
    // Update selection for state consistency
    onUpdateSelection({
      tier: localTier,
      period: localPeriod,
      package: pkg,
    });
    // Pass package directly to avoid race condition
    onPurchase(pkg);
  };

  // Set default package when offerings load
  React.useEffect(() => {
    if (offerings && !isInitialized) {
      // If user has Platinum, ensure we're selecting AI tier
      const effectiveTier = hasPlatinum ? 'ai' : localTier;
      const defaultPackage = resolvePackage(effectiveTier, localPeriod);
      if (defaultPackage) {
        onUpdateSelection({
          tier: effectiveTier,
          period: localPeriod,
          package: defaultPackage,
        });
      }
      setIsInitialized(true);
    }
  }, [offerings, isInitialized, hasPlatinum]);

  const selectedPackage = resolvePackage(localTier, localPeriod);
  const selectedHasTrial = selectedPackage ? SubscriptionService.hasTrialOffer(selectedPackage) : false;
  const trialDays = selectedPackage ? SubscriptionService.getTrialDays(selectedPackage) || 7 : 7;

  if (!offerings) {
    return (
      <View style={styles.container}>
        <PaywallHeader onClose={onClose} />
        <View style={styles.loading}>
          <ActivityIndicator size="large" color={Theme.colors.primary} />
          <Text variant="body1" style={styles.loadingText}>Loading plans...</Text>
        </View>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <PaywallHeader onClose={onClose} />
      
      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
      >
        <Text variant="h2" style={styles.title}>
          {hasPlatinum ? 'Upgrade to AI Platinum' : 'Choose Your Plan'}
        </Text>
        <Text variant="body1" style={styles.subtitle}>
          {hasPlatinum 
            ? 'Unlock AI-powered features and personalized recommendations' 
            : 'Select the plan that works best for you'}
        </Text>

        {/* Billing period toggle */}
        <View style={styles.periodToggle}>
          <Pressable
            style={[
              styles.periodButton,
              localPeriod === 'monthly' && styles.periodButtonActive,
            ]}
            onPress={() => setLocalPeriod('monthly')}
          >
            <Text
              variant="body1"
              style={[
                styles.periodText,
                localPeriod === 'monthly' && styles.periodTextActive,
              ]}
            >
              Monthly
            </Text>
          </Pressable>
          <Pressable
            style={[
              styles.periodButton,
              localPeriod === 'annual' && styles.periodButtonActive,
            ]}
            onPress={() => setLocalPeriod('annual')}
          >
            <Text
              variant="body1"
              style={[
                styles.periodText,
                localPeriod === 'annual' && styles.periodTextActive,
              ]}
            >
              Annual (Save 20%)
            </Text>
          </Pressable>
        </View>

        {/* Plan cards */}
        <View style={styles.plansContainer}>
          {/* Only show Platinum option if user doesn't already have it */}
          {!hasPlatinum && (
            <PlanCard
              tier="standard"
              period={localPeriod}
              priceString={getPrice('standard', localPeriod)}
              isSelected={localTier === 'standard'}
              onSelect={() => setLocalTier('standard')}
              hasTrial={getHasTrial('standard', localPeriod)}
            />
          )}

          {/* AI Platinum - always shown */}
          <PlanCard
            tier="ai"
            period={localPeriod}
            priceString={getPrice('ai', localPeriod)}
            isSelected={localTier === 'ai'}
            isRecommended={!hasPlatinum} // Only mark as recommended if showing both options
            onSelect={() => setLocalTier('ai')}
            hasTrial={getHasTrial('ai', localPeriod)}
            savingsPercent={localPeriod === 'annual' ? 20 : undefined}
          />
        </View>

        {/* CTA */}
        <Button
          title={
            isPurchasing
              ? 'Processing...'
              : selectedHasTrial
              ? `Start Your ${trialDays}-Day Free Trial`
              : 'Subscribe Now'
          }
          onPress={handlePurchase}
          disabled={isPurchasing || isRestoring || !selectedPackage}
          style={styles.ctaButton}
        />

        {isPurchasing && (
          <View style={styles.processingIndicator}>
            <ActivityIndicator size="small" color={Theme.colors.primary} />
            <Text variant="body2" color="secondary" style={styles.processingText}>
              Processing your subscription...
            </Text>
          </View>
        )}

        {/* Legal footer */}
        <LegalFooter
          hasTrial={selectedHasTrial}
          priceString={selectedPackage?.product.priceString || '...'}
          period={localPeriod}
          trialDays={selectedHasTrial ? trialDays : undefined}
          onRestore={onRestore}
          isRestoring={isRestoring}
        />
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Theme.colors.background,
  },
  loading: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    marginTop: Theme.spacing.md,
    color: Theme.colors.textSecondary,
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingTop: 80,
    paddingHorizontal: Theme.spacing.xxl,
    paddingBottom: Theme.spacing.xxxl,
  },
  title: {
    textAlign: 'center',
    marginBottom: Theme.spacing.sm,
  },
  subtitle: {
    textAlign: 'center',
    color: Theme.colors.textSecondary,
    marginBottom: Theme.spacing.xl,
  },
  periodToggle: {
    flexDirection: 'row',
    backgroundColor: Theme.colors.surfaceSecondary,
    borderRadius: Theme.borderRadius.lg,
    padding: Theme.spacing.xs,
    marginBottom: Theme.spacing.xl,
  },
  periodButton: {
    flex: 1,
    paddingVertical: Theme.spacing.sm,
    borderRadius: Theme.borderRadius.md,
    alignItems: 'center',
  },
  periodButtonActive: {
    backgroundColor: Theme.colors.primary,
  },
  periodText: {
    fontWeight: '600',
    color: Theme.colors.textPrimary,
  },
  periodTextActive: {
    color: Theme.colors.textInverse,
  },
  plansContainer: {
    marginBottom: Theme.spacing.xl,
  },
  ctaButton: {
    marginBottom: Theme.spacing.md,
  },
  processingIndicator: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: Theme.spacing.lg,
  },
  processingText: {
    marginLeft: Theme.spacing.sm,
  },
});

